<template>
  <base-form ref="form" v-model="isFormValid" :class="`grid grid-cols-${colSpan} gap-x-4 gap-y-1`">
    <base-text-field label="Name" placeholder="Enter name" v-model="name" v-if="!hideName" :class="`col-span-${colSpan}`" required />
    <div :class="`relative col-span-${colSpan}`">
      <label :class="['absolute -top-1 left-2 -mt-px inline-block px-1 bg-white text-xs font-normal z-20 rounded', { 'text-red-500': !!stripeElementsNumberError }]"
        >Card Number</label
      >
      <div ref="stripeElementsNumber" class="relative border rounded-md ring-0 transition-all ease-in-out duration-100 bg-white mt-1 z-10 cursor-text" />
      <div ref="stripeElementsNumberError" class="text-red-500 text-xs h-5 font-thin">
        <transition
          enter-from-class="-translate-y-full"
          enter-active-class="ease-out duration-300"
          enter-to-class="translate-y-0"
          leave-from-class="translate-y-0"
          leave-active-class="ease-in duration-100"
          leave-to-class="-translate-y-full"
        >
          <div class="transition-transform transform" v-show="stripeElementsNumberError">{{ stripeElementsNumberError }}</div>
        </transition>
      </div>
    </div>
    <div class="relative">
      <label :class="['absolute -top-1 left-2 -mt-px inline-block px-1 bg-white text-xs font-normal z-20 rounded', { 'text-red-500': !!stripeElementsExpirationError }]">Exp</label>
      <div ref="stripeElementsExpiration" class="relative border rounded-md ring-0 transition-all ease-in-out duration-100 bg-white mt-1 z-10 cursor-text" />
      <div ref="stripeElementsExpirationError" class="text-red-500 text-xs h-5 font-thin">
        <transition
          enter-from-class="-translate-y-full"
          enter-active-class="ease-out duration-300"
          enter-to-class="translate-y-0"
          leave-from-class="translate-y-0"
          leave-active-class="ease-in duration-100"
          leave-to-class="-translate-y-full"
        >
          <div class="transition-transform transform" v-show="stripeElementsExpirationError">{{ stripeElementsExpirationError }}</div>
        </transition>
      </div>
    </div>
    <div class="relative">
      <label :class="['absolute -top-1 left-2 -mt-px inline-block px-1 bg-white text-xs font-normal z-20 rounded', { 'text-red-500': !!stripeElementsCvcError }]">CVV / CVC</label>
      <div ref="stripeElementsCvc" class="relative border rounded-md ring-0 transition-all ease-in-out duration-100 bg-white mt-1 z-10 cursor-text" />
      <div ref="stripeElementsCvcError" class="text-red-500 text-xs h-5 font-thin">
        <transition
          enter-from-class="-translate-y-full"
          enter-active-class="ease-out duration-300"
          enter-to-class="translate-y-0"
          leave-from-class="translate-y-0"
          leave-active-class="ease-in duration-100"
          leave-to-class="-translate-y-full"
        >
          <div class="transition-transform transform" v-show="stripeElementsCvcError">{{ stripeElementsCvcError }}</div>
        </transition>
      </div>
    </div>
    <base-text-field label="Postal Code" placeholder="Enter postal code" v-model="postalCode" v-if="!hidePostalCode" required />
  </base-form>
</template>

<script>
import { loadStripe } from "@stripe/stripe-js/pure";

export default {
  props: {
    hideName: Boolean,
    hidePostalCode: Boolean,
  },
  data() {
    return {
      isFormValid: true,
      stripeToken: null,
      stripe: null,
      stripeElements: null,
      stripeElementsStyle: null,
      stripeElementsNumberError: null,
      stripeElementsExpirationError: null,
      stripeElementsCvcError: null,
      name: null,
      postalCode: null,
    };
  },
  computed: {
    colSpan() {
      return this.hidePostalCode ? 2 : 3;
    },
  },
  async mounted() {
    await this.loadStripeElements();

    this.$nextTick(() => {
      this.mountStripeElements();
    });
  },
  methods: {
    async loadStripeElements() {
      this.stripe = await loadStripe(this.$env.stripe_publishable_key);

      this.stripeElements = this.stripe.elements({
        fonts: [
          {
            cssSrc: "https://fonts.googleapis.com/css?family=Roboto:300,400,500,700",
          },
        ],
      });
      this.stripeElementsStyle = {
        base: {
          color: "rgb(17, 24, 39)",
          fontWeight: 300,
          fontFamily: 'Roboto, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
          fontSize: "14px",

          "::placeholder": {
            color: "rgb(107, 114, 128)",
          },
        },
        invalid: {
          color: "rgb(239, 68, 68)",
          "::placeholder": {
            color: "rgb(248, 113, 113)",
          },
        },
      };

      this.stripeElementsNumber = this.stripeElements.create("cardNumber", { style: this.stripeElementsStyle });
      this.stripeElementsNumber.addEventListener("change", (event) => (this.stripeElementsNumberError = event.error ? event.error.message : ""));

      this.stripeElementsExpiration = this.stripeElements.create("cardExpiry", { style: this.stripeElementsStyle });
      this.stripeElementsExpiration.addEventListener("change", (event) => (this.stripeElementsExpirationError = event.error ? event.error.message : ""));

      this.stripeElementsCvc = this.stripeElements.create("cardCvc", { style: this.stripeElementsStyle });
      this.stripeElementsCvc.addEventListener("change", (event) => (this.stripeElementsCvcError = event.error ? event.error.message : ""));
    },
    mountStripeElements() {
      this.stripeElementsNumber.mount(this.$refs.stripeElementsNumber);
      this.stripeElementsExpiration.mount(this.$refs.stripeElementsExpiration);
      this.stripeElementsCvc.mount(this.$refs.stripeElementsCvc);
    },
    async createToken() {
      const response = await this.stripe.createToken(this.stripeElementsNumber, {
        name: this.name || "",
        address_zip: this.postalCode || "",
        address_country: "US",
        currency: "usd",
      });
      this.$refs.form.validate();
      if (response.error) {
        this.stripeElementsNumberError = response.error.message;
        return null;
      }
      if (!this.isFormValid) {
        return null;
      }
      return response.token;
    },
    reset() {
      this.$refs.form.reset();
      this.stripeElementsNumber.clear();
      this.stripeElementsExpiration.clear();
      this.stripeElementsCvc.clear();
    },
  },
};
</script>

<!-- styles for .StripeElement* selectors live in src/assets/main.css
     (Tailwind 4 @apply doesn't work in SFC <style> blocks without @reference) -->
